import type { ItemKind } from '../constants/vocabulary';

export interface RegistryListFilters {
  readonly includeArchived: boolean;
}

const ROOT = ['ctl-config'] as const;

const libraryRoot = () => [...ROOT, 'library'] as const;
const libraryDetails = () => [...libraryRoot(), 'detail'] as const;
const libraryDetail = (code: string) => [...libraryDetails(), code] as const;
const plantRoot = (plantId: string) => [...ROOT, 'plant', plantId] as const;
const registryRoot = (plantId: string) => [...plantRoot(plantId), 'registry'] as const;
const registryLists = (plantId: string) => [...registryRoot(plantId), 'list'] as const;
const registryDetails = (plantId: string) => [...registryRoot(plantId), 'detail'] as const;

export const ctlConfigKeys = {
  all: () => ROOT,
  library: {
    all: libraryRoot,
    lists: () => [...libraryRoot(), 'list'] as const,
    details: libraryDetails,
    detail: libraryDetail,
    history: (code: string) => [...libraryDetail(code), 'history'] as const,
  },
  readings: () => [...ROOT, 'readings'] as const,
  plantTypes: () => [...ROOT, 'plant-types'] as const,
  nodeTypes: () => [...ROOT, 'node-types'] as const,
  plants: () => [...ROOT, 'plant'] as const,
  plantList: () => [...ROOT, 'plant-list'] as const,
  plant: plantRoot,
  analysis: (plantId: string) => [...plantRoot(plantId), 'analysis'] as const,
  registry: {
    all: registryRoot,
    lists: registryLists,
    list: (plantId: string, filters: RegistryListFilters) => [...registryLists(plantId), filters] as const,
    details: registryDetails,
    detail: (plantId: string, entryId: string) => [...registryDetails(plantId), entryId] as const,
  },
};

const mutationRoot = ['ctl-config', 'mutation'] as const;
const typeMutation = (code: string) => [...mutationRoot, 'library', code] as const;
const entryMutation = (plantId: string, entryId: string) => [...mutationRoot, 'registry', plantId, entryId] as const;

export const ctlConfigMutationKeys = {
  library: {
    create: () => [...mutationRoot, 'library-create'] as const,
    type: typeMutation,
    replace: (code: string) => [...typeMutation(code), 'replace'] as const,
    archive: (code: string) => [...typeMutation(code), 'archive'] as const,
  },
  scada: {
    apply: (plantId: string) => [...mutationRoot, 'scada', plantId, 'apply'] as const,
  },
  registry: {
    entry: entryMutation,
    tier: (plantId: string, entryId: string) => [...entryMutation(plantId, entryId), 'tier'] as const,
    override: (plantId: string, entryId: string, kind: ItemKind, key: string) =>
      [...entryMutation(plantId, entryId), 'override', kind, key] as const,
    maintenance: (plantId: string, entryId: string) => [...entryMutation(plantId, entryId), 'maintenance'] as const,
  },
};

const MINUTE = 60_000;

export const CTL_CONFIG_STALE = {
  libraryList: 5 * MINUTE,
  libraryType: 5 * MINUTE,
  history: 5 * MINUTE,
  readings: 30 * MINUTE,
  plantTypes: 10 * MINUTE,
  nodeTypes: 5 * MINUTE,
  analysis: Infinity,
  registryList: 30_000,
  registryEntry: 30_000,
  plantList: 30_000,
} as const;

export const CTL_CONFIG_GC = {
  standard: 10 * MINUTE,
  readings: 30 * MINUTE,
  analysis: 5 * MINUTE,
} as const;
